import { useCallback } from 'react'

import { faGear, faPlus } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button } from 'antd'
import { Tabs, Tooltip } from 'govwell-ui'
import { computed, runInAction } from 'mobx'
import { observer } from 'mobx-react-lite'
import styled from 'styled-components'
import { GetOrCreateViewsMutation } from 'types/graphql'

import CreateViewModal from 'src/components/TableViews/CreateViewModal'
import EditViewsDrawer from 'src/components/TableViews/EditViewsDrawer/EditViewsDrawer'
import TableViewTab from 'src/components/TableViews/TableViewTab'
import { TableViewMode } from 'src/fetch/views'
import { TabItem } from 'src/govwell-ui/components/Tabs'
import useDisclosure from 'src/hooks/use-disclosure'
import { TableView } from 'src/models/TableViews/TableView'
import { TableViewManager } from 'src/models/TableViews/TableViewManager'

const StyledActionButton = styled(Button)`
  width: 36px !important;
  min-width: 36px !important;
`

type Props = {
  generateDefaultViews: () => Promise<GetOrCreateViewsMutation>
  isLoading?: boolean
  mode: TableViewMode
  onSelectedViewChange?: (view: TableView) => void
  tableViewManager: TableViewManager
  viewCountLoadingStates?: Map<number, boolean>
}
const TableViewTabs = ({
  generateDefaultViews,
  isLoading,
  mode,
  onSelectedViewChange,
  tableViewManager,
  viewCountLoadingStates,
}: Props) => {
  const createViewModalState = useDisclosure()
  const editViewsDrawerState = useDisclosure()

  const handleSelectionChange = useCallback(
    (viewId: string) => {
      runInAction(() => {
        const isChanging = tableViewManager.selectedView?.id !== parseInt(viewId)
        if (!isChanging) {
          return
        }
        tableViewManager.setSelectedViewId(parseInt(viewId))
        if (tableViewManager.selectedView) {
          onSelectedViewChange?.(tableViewManager.selectedView)
        }
      })
    },
    [onSelectedViewChange, tableViewManager]
  )

  const handleReorderTabs = useCallback(
    (viewIds: string[]) => {
      runInAction(() => {
        tableViewManager.reorderViews(viewIds.map((id) => parseInt(id)))
      })
    },
    [tableViewManager]
  )

  const tabs: TabItem<string>[] = computed(() => {
    return tableViewManager.views.map(
      (v): TabItem<string> => ({
        key: `${v.id}`,
        count: v.databaseTotalCount,
        countIsLoading: viewCountLoadingStates?.get(v.id) ?? false,
        label: v.name,
        renderTab: ({ children }) => (
          <TableViewTab
            key={v.id}
            view={v}
            generateDefaultViews={generateDefaultViews}
            mode={mode}
            tableViewManager={tableViewManager}
          >
            {children}
          </TableViewTab>
        ),
      })
    )
  }).get()

  return (
    <>
      <Tabs
        actions={
          mode === TableViewMode.Edit ? (
            <>
              <Tooltip content="Add view">
                <StyledActionButton
                  className="govwell-intercom-saved-table-view-tooltip-target"
                  data-testid="table-view-add-view-button"
                  icon={<FontAwesomeIcon icon={faPlus} />}
                  onClick={createViewModalState.open}
                />
              </Tooltip>
              <Tooltip content="Edit saved views">
                <StyledActionButton
                  data-testid="table-view-edit-views-button"
                  icon={<FontAwesomeIcon icon={faGear} />}
                  onClick={editViewsDrawerState.open}
                />
              </Tooltip>
            </>
          ) : undefined
        }
        isLoading={isLoading}
        onReorderTabs={handleReorderTabs}
        onSelectedTabKeyChange={handleSelectionChange}
        selectedTabKey={`${tableViewManager.selectedView?.id}`}
        tabs={tabs}
      />
      {createViewModalState.isOpen && (
        <CreateViewModal tableViewManager={tableViewManager} modalState={createViewModalState} />
      )}
      {editViewsDrawerState.isOpen && (
        <EditViewsDrawer
          generateDefaultViews={generateDefaultViews}
          modalState={editViewsDrawerState}
          tableViewManager={tableViewManager}
        />
      )}
    </>
  )
}

export default observer(TableViewTabs)
